import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { CalendarEvent, CalendarView } from 'angular-calendar';
import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns';
import { environment } from 'src/environments/environment';
import { ModalService } from 'src/app/core/services/modal.service';
import { CalendarItem, InspectionType } from '../../models/inspection.models';
import { VehicleInspectionService } from '../../services/vehicle-inspection.service';
import { InspectionActionModalComponent, InspectionTarget } from '../../modals/inspection-action-modal/inspection-action-modal.component';

const WEEK_STARTS_ON = 1; // Monday
const OVERDUE = '#d9534f';

/** Every vehicle's MOT and inspections on a month / week calendar; click one to mark it done. */
@Component({
    selector: 'app-inspection-calendar',
    templateUrl: './inspection-calendar.component.html',
    styleUrls: ['./inspection-calendar.component.scss'],
    standalone: false
})
export class InspectionCalendarComponent implements OnInit {
  @ViewChild(InspectionActionModalComponent) modal: InspectionActionModalComponent;

  readonly CalendarView = CalendarView;
  readonly weekStartsOn = WEEK_STARTS_ON;
  view: CalendarView = CalendarView.Month;
  viewDate: Date = new Date();

  events: CalendarEvent<CalendarItem>[] = [];
  types: InspectionType[] = [];
  vehicles: { id: number, registration: string }[] = [];
  typeId: number = -1;
  vehicleId: number = -1;
  overdueCount: number = 0;
  loading: boolean = false;
  error: string = '';

  target: InspectionTarget | null = null;

  constructor(private service: VehicleInspectionService, private http: HttpClient, private modalService: ModalService) { }

  ngOnInit(): void {
    this.service.getTypes().pipe(take(1)).subscribe((t) => this.types = t);
    this.http.get<any[]>(environment.api.endpoint + 'vehicle?sort=registration,ASC').pipe(take(1))
      .subscribe((v) => this.vehicles = v.map((x) => ({ id: x.id, registration: x.registration || x.name || ('Vehicle #' + x.id) })));
    this.load();
  }

  /** Loads the visible range: the whole weeks around the month, or the week. */
  load(): void {
    const from = this.view === CalendarView.Month
      ? startOfWeek(startOfMonth(this.viewDate), { weekStartsOn: WEEK_STARTS_ON })
      : startOfWeek(this.viewDate, { weekStartsOn: WEEK_STARTS_ON });
    const to = this.view === CalendarView.Month
      ? endOfWeek(endOfMonth(this.viewDate), { weekStartsOn: WEEK_STARTS_ON })
      : endOfWeek(this.viewDate, { weekStartsOn: WEEK_STARTS_ON });

    this.loading = true;
    this.error = '';
    this.service.calendar(format(from, 'yyyy-MM-dd'), format(to, 'yyyy-MM-dd'),
      { inspectionTypeId: Number(this.typeId), vehicleId: Number(this.vehicleId) })
      .pipe(take(1)).subscribe({
        next: (items) => {
          this.events = items.map((item) => this.toEvent(item));
          this.overdueCount = items.filter((i) => i.overdue).length;
          this.loading = false;
        },
        error: () => {
          this.error = 'The calendar could not be loaded. Please try again.';
          this.loading = false;
        },
      });
  }

  toEvent(item: CalendarItem): CalendarEvent<CalendarItem> {
    const [y, m, d] = item.nextDueDate.split('-').map(Number);
    const colour = item.overdue ? OVERDUE : item.colour;
    return {
      start: new Date(y, m - 1, d), // local midnight: the calendar day it is due, no timezone shift
      allDay: true,
      title: `${item.overdue ? 'OVERDUE · ' : ''}${item.registration} – ${item.typeName}`,
      color: { primary: colour, secondary: colour + '22' },
      cssClass: item.overdue ? 'inspectionOverdue' : '',
      meta: item,
    };
  }

  setView(view: CalendarView): void {
    this.view = view;
    this.load();
  }

  onViewDateChange(): void {
    this.load();
  }

  eventClicked({ event }: { event: CalendarEvent<CalendarItem> }): void {
    const item = event.meta;
    this.target = { id: item.id, registration: item.registration, typeName: item.typeName, nextDueDate: item.nextDueDate, lastDoneDate: item.lastDoneDate };
    setTimeout(() => {
      this.modal.reset();
      this.modalService.open('inspectionAction');
    });
  }

  legend(): InspectionType[] {
    return this.types.filter((t) => t.active);
  }
}
